import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import PropTypes from 'prop-types';

import { InputField } from 'components/FormHelper/InputField';

import { MISSING_PROVIDER_OBJECT, NO_RESULTS_SENTINEL } from 'global/constants';

import './ComboBox.sass';

export const ComboBox = (props) => {
  const {
    options,
    value,
    onChange,
    onInputChange,
    name,
    label,
    error,
    disabled,
    oldValue,
    isEdited,
    helperText,
    loading,
    readOnlyInput,
    ...rest
  } = props;
  const selectedOption = () => {
    if (value === -8888) return MISSING_PROVIDER_OBJECT;
    return (
      options.find((option) => String(option.value) === String(value) || option.title === value) ??
      null
    );
  };
  const filterFunction = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.title || '',
  });
  const gridProviderFields = ['gridProviderEnergy', 'gridProviderGas'];
  const [hasInput, setHasInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchCompleted, setSearchCompleted] = useState(false);
  const prevLoadingRef = useRef(false);
  const hasInitializedInputRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const comboSelectedOption = selectedOption();
  const oldSelectedOption = options.find(
    (option) => String(option.value) === String(oldValue) || option.title === oldValue
  );
  useEffect(() => {
    if (hasInitializedInputRef.current) return;
    if (!comboSelectedOption?.title) return;

    setInputValue(comboSelectedOption.title);
    setHasInput(Boolean(comboSelectedOption.title));
    hasInitializedInputRef.current = true;
  }, [comboSelectedOption]);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setSearchCompleted(true);
    }

    prevLoadingRef.current = loading;
  }, [loading]);

  return (
    <Autocomplete
      id={name}
      freeSolo
      loadingText="Wird geladen..."
      noOptionsText={hasInput ? 'Keine Ergebnisse gefunden' : ''}
      filterOptions={(allOptions, state) => {
        const isGridProviderField = gridProviderFields.includes(name);
        const isSentinelField = allOptions.some((o) => o.value === NO_RESULTS_SENTINEL.value);
        const effectiveInputValue = state.inputValue;
        if (loading) {
          return [];
        }
        const realOptions = allOptions.filter(
          (o) => o.value !== MISSING_PROVIDER_OBJECT.value && o.value !== NO_RESULTS_SENTINEL.value
        );
        if (!effectiveInputValue) {
          if (isGridProviderField) return [...realOptions, MISSING_PROVIDER_OBJECT];
          return realOptions;
        }
        const filtered = filterFunction(realOptions, {
          ...state,
          inputValue: effectiveInputValue,
        });
        if (isGridProviderField) {
          return [...filtered, MISSING_PROVIDER_OBJECT];
        }
        if (isSentinelField) {
          return filtered;
        }
        if (filtered.length === 0) {
          return [NO_RESULTS_SENTINEL];
        }
        return filtered;
      }}
      options={options}
      name={name}
      getOptionLabel={(option) => option.title}
      getOptionDisabled={(option) => option === NO_RESULTS_SENTINEL}
      onChange={(e, newVal) => {
        if (newVal?.value === NO_RESULTS_SENTINEL.value) return;
        onChange(name, newVal?.value || '');
      }}
      onInputChange={(e, val, reason) => {
        setInputValue(val);
        setHasInput(Boolean(val));
        setSearchCompleted(false);
        if (reason === 'clear') {
          setIsOpen(true);
        }
        if (onInputChange) onInputChange(e, val, reason);
      }}
      loading={loading}
      disabled={disabled}
      value={comboSelectedOption}
      inputValue={inputValue}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      {...rest}
      renderInput={(params) => (
        <InputField
          label={label}
          name={name}
          fullWidth
          error={error}
          isEdited={isEdited}
          helperText={helperText}
          {...rest}
          {...params}
          InputProps={{
            ...params.InputProps,
            readOnly: readOnlyInput,
          }}
          oldValue={String(oldSelectedOption?.value ?? oldValue)}
          value={String(comboSelectedOption?.value)}
        />
      )}
    />
  );
};

ComboBox.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  oldValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Callback fired when the value changes. Users should pass `formik.setFieldValue`
   * here and the new event will be handled by the component
   *
   * @param {object} event The event source of the callback.
   * @param {any} newValue The new value of the component. .
   */
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  isEdited: PropTypes.bool,
  disabled: PropTypes.bool,
  onInputChange: PropTypes.func,
  loading: PropTypes.bool,
  readOnlyInput: PropTypes.bool,
};

ComboBox.defaultProps = {
  isEdited: false,
  disabled: false,
  error: false,
  helperText: '',
  oldValue: -1,
  onInputChange: undefined,
  loading: false,
  readOnlyInput: false,
};
